import { useEffect, useState } from "react";
import { ArrowLeft, CalendarClock, Eye, Loader2, Save, Send, Trash2, Undo2 } from "lucide-react";
import { staffApi, errMsg, fmtDate } from "../api";
import { Field, Status } from "../Shell";
import { RichEditor } from "./RichEditor";
import { FeaturedPhoto, GalleryManager } from "./PhotoManager";
import { PreviewDialog, toView } from "./PreviewDialog";

const EMPTY_EVENT = { start: "", end: "", location: "", link_url: "", link_label: "" };

const fromArticle = (a, cats) => ({
  title: a?.title ?? "", summary: a?.raw_summary ?? "", body: a?.body ?? "", recap: a?.recap ?? "",
  category: a?.raw_category ?? cats[0]?.slug ?? "", featured: a?.featured ?? null, gallery: a?.gallery ?? [],
  hasEvent: !!a?.event, event: { ...EMPTY_EVENT, ...(a?.event ?? {}), end: a?.event?.end ?? "" },
  seo_title: a?.raw_seo_title ?? "", seo_description: a?.raw_seo_description ?? "",
});

const ref = (p) => (p ? { photo: p.photo, alt: p.alt ?? "", caption: p.caption ?? "" } : null);

const toPayload = (f) => ({
  title: f.title, summary: f.summary, body: f.body, recap: f.recap, category: f.category,
  featured: ref(f.featured), gallery: f.gallery.map(ref),
  event: f.hasEvent ? { ...f.event, end: f.event.end || null } : null,
  seo_title: f.seo_title, seo_description: f.seo_description,
});

const Section = ({ title, hint, children }) => (
  <section className="border border-navy/10 bg-white p-5 md:p-6">
    <h3 className="font-semibold text-navy">{title}</h3>
    {hint && <p className="mt-1 text-xs text-slate">{hint}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const stateLabel = (a) => {
  if (!a || a.status !== "published") return "Draft (not visible to the public)";
  return new Date(a.publish_at) > new Date() ? `Scheduled for ${fmtDate(a.publish_at)}` : `Published ${fmtDate(a.publish_at)}`;
};

const EventFields = ({ ev, set }) => (
  <div className="grid gap-4 md:grid-cols-2">
    <Field label="Starts (Eastern time)" type="datetime-local" required value={ev.start} onChange={(e) => set({ ...ev, start: e.target.value })} data-testid="editor-event-start" />
    <Field label="Ends (optional)" type="datetime-local" value={ev.end} onChange={(e) => set({ ...ev, end: e.target.value })} data-testid="editor-event-end" />
    <div className="md:col-span-2"><Field label="Location" maxLength={200} value={ev.location} onChange={(e) => set({ ...ev, location: e.target.value })} data-testid="editor-event-location" /></div>
    <Field label="Registration or donation link (optional)" type="url" placeholder="https://" maxLength={500} value={ev.link_url} onChange={(e) => set({ ...ev, link_url: e.target.value })} data-testid="editor-event-link" />
    <Field label="Button text" placeholder="Register" maxLength={40} value={ev.link_label} onChange={(e) => set({ ...ev, link_label: e.target.value })} data-testid="editor-event-link-label" />
  </div>
);

export const ArticleEditor = ({ articleId, cats, onBack }) => {
  const [article, setArticle] = useState(null);
  const [form, setForm] = useState(fromArticle(null, cats));
  const [loading, setLoading] = useState(!!articleId);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState({});
  const [schedule, setSchedule] = useState("");
  const [preview, setPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    staffApi.get(`/news/${articleId}`)
      .then((r) => { setArticle(r.data); setForm(fromArticle(r.data, cats)); })
      .catch(async (e) => setStatus({ state: "error", msg: await errMsg(e) }))
      .finally(() => setLoading(false));
  }, [articleId, cats]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const set = (key, v) => { setDirty(true); setForm((f) => ({ ...f, [key]: typeof v === "function" ? v(f[key]) : v })); };

  const save = async () => {
    if (!form.title.trim()) throw new Error("Please add a title.");
    if (form.hasEvent && !form.event.start) throw new Error("Please add the event start date and time.");
    const { data } = article
      ? await staffApi.put(`/news/${article.id}`, toPayload(form))
      : await staffApi.post("/news", toPayload(form));
    setArticle(data);
    setDirty(false);
    return data;
  };

  const run = async (fn, msg) => {
    setStatus({ state: "loading" });
    try { await fn(); setStatus({ state: "success", msg }); } catch (err) { setStatus({ state: "error", msg: err?.response ? await errMsg(err) : err.message }); }
  };

  const publish = (when) => run(async () => {
    const a = await save();
    const { data } = await staffApi.post(`/news/${a.id}/publish`, when ? { publish_at: when } : {});
    setArticle(data);
    setSchedule("");
  }, when ? "Scheduled. It will appear automatically at that time." : "Published. It's now live on the News & Events page.");

  const back = () => { if (!dirty || window.confirm("Leave without saving your changes?")) onBack(); };
  const remove = () => run(async () => { await staffApi.delete(`/news/${article.id}`); onBack(); }, "Deleted.");

  if (loading) return <Loader2 className="animate-spin text-navy" />;
  const published = article?.status === "published";
  return (
    <div className="max-w-4xl space-y-6" data-testid="news-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={back} className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold-deep" data-testid="editor-back-button"><ArrowLeft size={16} /> All articles</button>
        <p className="text-sm text-slate" data-testid="editor-state">{stateLabel(article)}{dirty && " · Unsaved changes"}</p>
      </div>

      <Section title="Article">
        <Field label="Title" required maxLength={160} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title-input" />
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-navy">Category</span>
          <select className="field-input" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category-select">
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-navy">Short description (shown on cards)</span>
          <textarea className="field-input min-h-[90px]" maxLength={300} value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="editor-summary-input" />
          <span className="mt-1 block text-xs text-slate">{form.summary.length}/300</span>
        </label>
      </Section>

      <Section title="Featured photo" hint="Shown on the card, at the top of the article and when the link is shared.">
        <FeaturedPhoto value={form.featured} onChange={(v) => set("featured", v)} />
      </Section>

      <Section title="Story">
        <RichEditor value={form.body} onChange={(v) => set("body", v)} placeholder="Write the article here..." testId="editor-body" />
      </Section>

      <Section title="Event details" hint="Turn on for fundraisers and community events. Upcoming events get an 'Upcoming Event' badge.">
        <label className="flex items-center gap-3 text-sm font-semibold text-navy">
          <input type="checkbox" checked={form.hasEvent} onChange={(e) => set("hasEvent", e.target.checked)} data-testid="editor-event-toggle" />
          This article is about an event
        </label>
        {form.hasEvent && <EventFields ev={form.event} set={(v) => set("event", v)} />}
      </Section>

      {form.hasEvent && (
        <Section title="Event recap (after the event)" hint="Add a recap and photos here once the event is over. There's no need to create a new article.">
          <RichEditor value={form.recap} onChange={(v) => set("recap", v)} placeholder="How did the event go?" testId="editor-recap" />
        </Section>
      )}

      <Section title="Photo gallery" hint="Use Earlier / Later to change the order.">
        <GalleryManager value={form.gallery} onChange={(v) => set("gallery", v)} />
      </Section>

      <Section title="Google and sharing (optional)" hint="Leave blank to use the title and short description.">
        <Field label="Search title" maxLength={70} value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="editor-seo-title" hint={`${form.seo_title.length}/70`} />
        <Field label="Search description" maxLength={170} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="editor-seo-description" hint={`${form.seo_description.length}/170`} />
        {published && article?.slug && <p className="text-xs text-slate">Web address: <span className="font-mono">/news/{article.slug}</span></p>}
      </Section>

      <div className="sticky bottom-0 z-10 space-y-3 border border-navy/10 bg-paper/95 p-4 backdrop-blur" data-testid="editor-actions">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => run(save, published ? "Changes saved. They're live now." : "Draft saved.")} className="btn btn-ghost" data-testid="editor-save-button"><Save size={16} /> {published ? "Save Changes" : "Save Draft"}</button>
          <button type="button" onClick={() => setPreview(true)} className="btn btn-ghost" data-testid="editor-preview-button"><Eye size={16} /> Preview</button>
          {!published && <button type="button" onClick={() => publish(null)} className="btn btn-navy" data-testid="editor-publish-button"><Send size={16} /> Publish Now</button>}
          {published && <button type="button" onClick={() => run(async () => { const { data } = await staffApi.post(`/news/${article.id}/unpublish`); setArticle(data); }, "Unpublished. It's no longer visible to the public.")} className="btn btn-ghost" data-testid="editor-unpublish-button"><Undo2 size={16} /> Unpublish</button>}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-navy">Or publish later (Eastern time)</span>
            <input type="datetime-local" className="field-input !py-2 text-sm" value={schedule} onChange={(e) => setSchedule(e.target.value)} data-testid="editor-schedule-input" />
          </label>
          <button type="button" disabled={!schedule} onClick={() => publish(schedule)} className="btn btn-ghost disabled:opacity-40" data-testid="editor-schedule-button"><CalendarClock size={16} /> Schedule</button>
          {article && (confirmDelete ? (
            <span className="ml-auto flex gap-2">
              <button type="button" onClick={remove} className="btn btn-red" data-testid="editor-confirm-delete-button">Delete permanently</button>
              <button type="button" onClick={() => setConfirmDelete(false)} className="btn btn-ghost" data-testid="editor-cancel-delete-button">Cancel</button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} className="btn btn-ghost ml-auto" data-testid="editor-delete-button"><Trash2 size={16} /> Delete</button>
          ))}
        </div>
        <Status status={status} testId="editor-status" />
      </div>
      <PreviewDialog open={preview} onClose={() => setPreview(false)} view={toView(form, cats, published ? article.publish_at : null)} />
    </div>
  );
};
